<template>
  <div
    class="bracelet-finder critical-component-clear"
    :class="[`bracelet-finder--step-${currentStep}`]"
  >
    <bracelet-finder-progress-bar
      :step="currentStep"
      @previous-step="handlePreviousStep"
    />

    <h1
      class="bracelet-finder__header text-body-l-medium"
      v-text="heading"
    />

    <component
      :is="currentComponent"
      v-bind="propsObject"
      :variant="variant || undefined"
      @confirm-step="handleConfirmStep"
      @previous-step="handlePreviousStep"
      @update-request-object="updateRequestObject"
    />
  </div>
</template>

<script>

/**
* Vue: Bracelet finder (bracelet-finder)
* -----------------------------------------------------------------------------
* Bracelet Finder Section based off of meta-object.
*
* @param {String} liquid.sectionId - Section's Liquid ID.
*
*/

import metaobjectQuery from '~graphql/metaobject.gql'
import { getMetafieldIDs, getVariables } from '~/helpers/graphql'
import { formatProduct } from '~/helpers/format'
import { convertToHandle } from '~/helpers/convert'

import BraceletFinderProgressBar from './bracelet-finder-progress-bar.vue'
import BraceletFinderStepOne from '~async/bracelet-finder/bracelet-finder-step-one'
import BraceletFinderStepTwo from '~async/bracelet-finder/bracelet-finder-step-two'
import MainProduct from '~async/main-product/main-product'

export default {
  name: 'BraceletFinder',

  components: {
    BraceletFinderProgressBar,
    BraceletFinderStepOne,
    BraceletFinderStepTwo,
    MainProduct,
  },

  props: {
    bracelets: {
      type: Array,
      default: () => [],
    },
    stones: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      chosenMetal: '',
      chosenImages: [],
      chosenProductImages: [],
      allProducts: [],
      variant: null,
      chosenProduct: {},
      currentComponent: 'bracelet-finder-step-one',
      currentStep: 1,
      propsObject: {
        bracelets: this.bracelets,
      },
    }
  },

  computed: {

    /**
     * Compute bracelet finder heading
     * @returns {string}
     */
    heading() {
      if (this.currentStep === 1) {
        return this.$string('bracelet_finder.step_1_header')
      } else if (this.currentStep === 2) {
        return this.$string('bracelet_finder.step_2_header')
      }
      return this.$string('bracelet_finder.step_3_header')
    },
  },

  created() {
    this.init()

    window.addEventListener('popstate', () => {
      this.init()
    })
  },

  methods: {
    init() {
      const urlParameters = new URLSearchParams(location.search)

      if (urlParameters.get('step') === '2') {
        const metalSelection = urlParameters.get('metal').replace('+', ' ')
        this.handleConfirmStep({ step: 1, selection: metalSelection })
      } else if (urlParameters.get('step') === '3') {
        const metalSelection = urlParameters.get('metal').replace('+', ' ')
        const productId = urlParameters.get('pid')
        this.handleStep3(metalSelection, productId)
      } else {
        this.currentComponent = 'bracelet-finder-step-one'
        this.propsObject = {
          bracelets: this.bracelets,
        }
        this.currentStep = 1
      }
    },

    handleConfirmStep(response) {
      if (response.step === 1) {
        const chosenBracelet = this.bracelets.find(
          (selected) => selected.metal_type === response.selection,
        )
        this.chosenImages = chosenBracelet.gaugeImages
        this.chosenMetal = response.selection
        this.chosenProductImages = chosenBracelet.gaugeProductImages
        this.currentComponent = 'bracelet-finder-step-two'
        this.propsObject = {
          metal: this.chosenMetal,
          gaugeProductImages: this.chosenProductImages,
          gaugeImages: this.chosenImages,
        }
        this.currentStep = 2
        window.scrollTo(0, 0)
      } else {
        this.chosenProduct = response.selection
        this.currentComponent = 'main-product'
        this.propsObject = {
          liquid: this.chosenProduct,
          stoneImages: this.stones,
          braceletFinder: true,
          section: {
            initialSelect: false,
          },
        }
        this.currentStep = 3
        window.scrollTo(0, 0)
      }
    },

    handlePreviousStep(response) {
      if (response.step === 1) {
        this.currentComponent = 'bracelet-finder-step-one'
        this.currentStep = 1
        this.propsObject = {
          bracelets: this.bracelets,
        }
        window.scrollTo(0, 0)
      } else {
        if (this.chosenMetal === '') {
          const urlParams = new URLSearchParams(location.search)
          this.chosenMetal = urlParams.get('metal').replace('+', ' ')

          const chosenBracelet = this.bracelets.find(
            (selected) => selected.metal_type === this.chosenMetal,
          )

          this.chosenImages = chosenBracelet.gaugeImages
          this.chosenProductImages = chosenBracelet.gaugeProductImages
        }
        this.currentComponent = 'bracelet-finder-step-two'
        this.currentStep = 2
        this.propsObject = {
          metal: this.chosenMetal,
          gaugeProductImages: this.chosenProductImages,
          gaugeImages: this.chosenImages,
        }
        window.scrollTo(0, 0)
      }
    },

    async handleStep3(metalSelection, productId) {
      await this.getProducts(metalSelection)
      const product = this.allProducts.find((product) => product.id.toString() === productId)
      this.handleConfirmStep({ step: 2, selection: product })
    },

    async getProducts(metal) {
      try {
        const response = await cnvs.Query({
          query: metaobjectQuery,
          variables: getVariables({
            handle: {
              type: 'bracelet_finder',
              handle: convertToHandle(metal),
            },
            metafieldIds: [
              ...getMetafieldIDs('product'),
              ...getMetafieldIDs('variant'),
            ],
          }),
        })

        const braceletMetaFields = response.metaobject.fields
        let gaugeProducts = braceletMetaFields.find((field) => field.key === 'gauge_product_list')
        gaugeProducts = gaugeProducts.references
        gaugeProducts = gaugeProducts.nodes.map(
          (product) => formatProduct({ product }),
        )
        this.allProducts = gaugeProducts
      } catch (error) {
        cnvs.ReportError('[Bracelet Finder]', error)
      }
    },

    /**
     * Handle response
     * @param {object} payload
     */
    updateRequestObject(payload) {
      if (!payload?.type) {
        return
      }

      if (payload.type === 'variant') {
        this.variant = payload.response
        cnvs.braceletFinder.variant = payload.response
      }

      if (payload.type === 'product') {
        this.chosenProduct = payload.response
        cnvs.braceletFinder.product = payload.response
        this.variant = null
        cnvs.braceletFinder.variant = null

        this.propsObject = {
          liquid: this.chosenProduct,
          stoneImages: this.stones,
          braceletFinder: true,
        }
      }
    },
  },

}
</script>

<style lang="scss">
  @import './bracelet-finder';
</style>
